import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const EDITORIAL = [
  {
    tag: "Signature Drop", line1: "BE RARE.", line2: "BE VENVAY.", accent: "VENVAY.",
    body: "Premium clothing crafted for those who choose to stand out.",
    img: "https://images.unsplash.com/photo-1618354691373-d851c5c3a990?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bg: "#2A231D", fg: "#F0E5D0", accentColor: "#D4B25C",
  },
  {
    tag: "The Manifesto", line1: "SIMPLICITY IS OUR", line2: "SIGNATURE.", accent: "SIGNATURE.",
    body: "Restraint over noise — the Venvay codebook.",
    img: "https://images.unsplash.com/photo-1516257984-b1b4d707412e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bg: "#0F0B08", fg: "#F0E5D0", accentColor: "#D4B25C",
  },
  {
    tag: "Studio · India", line1: "CRAFTED TO STAND", line2: "APART.", accent: "APART.",
    body: "Every stitch, every V, hand-finished in Venvay's studio.",
    img: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bg: "#2A231D", fg: "#F0E5D0", accentColor: "#D4B25C",
  },
  {
    tag: "Wear Yours", line1: "ELEVATE YOUR", line2: "EVERYDAY.", accent: "EVERYDAY.",
    body: "The heavyweight essentials you'll reach for every morning.",
    img: "https://images.unsplash.com/photo-1614495039944-a4c8f45c8e6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bg: "#3B2E22", fg: "#F0E5D0", accentColor: "#D4B25C",
  },
];

function EditorialPanel({ h, reversed }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-[60vh] group" style={{ background: h.bg, color: h.fg }}>
      <div className={`flex flex-col justify-center px-8 sm:px-16 py-16 ${reversed ? "md:order-2" : ""}`}>
        <p className="text-[10px] tracking-[0.4em] uppercase mb-6 opacity-70">{h.tag}</p>
        <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
          {h.line1}
          <br />
          {h.line2.replace(h.accent, "")}
          <span style={{ color: h.accentColor }}>{h.accent}</span>
        </h2>
        <p className="text-sm mt-6 max-w-sm opacity-75">{h.body}</p>
      </div>
      <div className={`relative overflow-hidden ${reversed ? "md:order-1" : ""}`}>
        <img src={h.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
    </div>
  );
}

export default function Signature() {
  const [all, setAll] = useState([]);
  useSEO({
    title: "The Signature Collection · Venvay Official",
    description: "The official Venvay Signature Collection — house-designed hoodies, oversized tees, sweatshirts and shirts carrying the Venvay V. Limited runs, quiet luxury.",
    keywords: "venvay signature collection, venvay official, venvay hoodie, venvay v tee, be rare be venvay, premium indian streetwear",
    canonicalPath: "/signature",
  });

  useEffect(() => {
    api.get("/products").then((r) => setAll(r.data.filter((p) => (p.tags || []).includes("signature"))));
  }, []);

  const men = useMemo(() => all.filter((p) => p.category === "men"), [all]);
  const women = useMemo(() => all.filter((p) => p.category === "women"), [all]);
  const gifts = useMemo(() => all.filter((p) => p.category === "gifts"), [all]);

  return (
    <div>
      {/* Editorial hero */}
      <div className="relative bg-[#0F0B08] text-[#F0E5D0] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#D4B25C] mb-6">
            <Sparkles size={14} /> Official · Venvay Signature
          </div>
          <h1 className="font-display font-black text-6xl sm:text-8xl leading-[0.9] tracking-tight">
            THE <span className="text-gold">SIGNATURE</span><br />
            COLLECTION.
          </h1>
          <p className="mt-6 text-white/70 max-w-xl mx-auto italic">
            Our house-designed hoodies, tees, sweatshirts and shirts — carrying the Venvay V. Limited runs, quiet luxury.
          </p>
          <Link to="/customize" data-testid="sig-customize-cta" className="inline-block mt-10 px-10 py-4 border border-[#D4B25C]/40 hover:bg-[#D4B25C] hover:text-[#0F0B08] transition-colors text-xs tracking-[0.4em] uppercase font-bold text-[#D4B25C]">
            Customize a Signature
          </Link>
        </div>
      </div>

      {/* Editorial panels */}
      {EDITORIAL.map((h, i) => (
        <Reveal key={i}><EditorialPanel h={h} reversed={i % 2 === 1} /></Reveal>
      ))}

      {/* Product grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <div className="text-center mb-10">
            <p className="label-tag">Shop the Signature</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black mt-2">Own a <span className="text-gold">Venvay original.</span></h2>
          </div>
        </Reveal>

        <Tabs defaultValue="all">
          <TabsList className="mx-auto">
            <TabsTrigger data-testid="sig-tab-all" value="all">All ({all.length})</TabsTrigger>
            <TabsTrigger data-testid="sig-tab-men" value="men">Men ({men.length})</TabsTrigger>
            <TabsTrigger data-testid="sig-tab-women" value="women">Women ({women.length})</TabsTrigger>
            <TabsTrigger data-testid="sig-tab-gifts" value="gifts">Gifts ({gifts.length})</TabsTrigger>
          </TabsList>
          {[["all", all], ["men", men], ["women", women], ["gifts", gifts]].map(([k, arr]) => (
            <TabsContent key={k} value={k} className="mt-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {arr.map((p, idx) => (
                  <Reveal key={p.id} delay={idx * 60}><ProductCard p={p} /></Reveal>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Bottom conviction panel */}
      <section className="bg-[#0F0B08] text-[#F0E5D0] py-24 text-center border-t border-[#D4B25C]/10">
        <p className="label-tag text-[#D4B25C]/60 mb-6">The Venvay Codebook</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-none">
          NOT FOR EVERYONE.<br />JUST FOR <span className="text-[#D4B25C]">YOU.</span>
        </h2>
        <Link to="/men" data-testid="sig-shop-btn" className="inline-block mt-10 px-10 py-4 border border-[#D4B25C]/40 hover:bg-[#D4B25C] hover:text-[#0F0B08] transition-colors text-xs tracking-[0.4em] uppercase font-bold text-[#D4B25C]">
          Shop the Signature <ArrowRight size={14} className="inline ml-2" />
        </Link>
      </section>
    </div>
  );
}
